import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A cursor over a list of tasks, and a selection under it.
 *
 * This is the mechanism the whole clutter fix rests on. Before it, every verb had to be printed
 * on every row, because a row was the only thing that could be acted upon — which put 137 pixels
 * of controls on each of ten rows to serve an action taken on one task at a time. With a cursor,
 * the verbs live in one place and apply to whatever is under it.
 *
 * It is also what makes transitions cheap. The measured cost of moving a task was two clicks plus
 * reading a three-option menu; with a cursor it is one keystroke, and the same keystroke works on
 * a multi-selection, so seven stale tasks are one decision rather than seven.
 *
 * Two rules keep it honest:
 *
 * The cursor is clamped, never wrapped. Tasks disappear from under it constantly — completing one
 * removes it from a list that hides done work — so the ids are reconciled on every change and the
 * cursor lands on the next surviving row rather than jumping to the far end of the list.
 *
 * Selection is by id, not index. A refetch reorders rows; anything holding indices would silently
 * act on the wrong tasks.
 */

/**
 * Where the cursor goes when the list changes underneath it.
 *
 * Pure, and exported, because this is the only genuinely fiddly part and it deserves a test that
 * does not need a renderer. `previousIds` is what the list looked like when the cursor was last
 * valid — without it there is no way to know which row has taken the vanished one's place.
 */
export function reconcileCursor(
  current: string | null,
  ids: readonly string[],
  previousIds: readonly string[],
): string | null {
  if (ids.length === 0) return null;
  if (current !== null && ids.includes(current)) return current;
  if (current === null) return ids[0]!;
  /*
    The cursor's row is gone. Land on whatever occupies its old position rather than resetting to
    the top: completing the fourth task should leave the cursor on the fourth row, which is the
    task that just moved up into it.
  */
  const wasAt = previousIds.indexOf(current);
  if (wasAt === -1) return ids[0]!;
  return ids[Math.min(wasAt, ids.length - 1)]!;
}

/**
 * Which selected ids survive a new list.
 *
 * Pure and exported for the same reason `reconcileCursor` is: it is the whole of the rule, and it
 * deserves a test that does not need a renderer.
 *
 * The old rule was one line — keep the ids the list currently draws — and it conflated *not shown*
 * with *not there*. Selection is by id and a narrowing filter removes no tasks, so ticking
 * "Unfiled" over a twelve-row selection threw all twelve away: react-query hands back
 * `undefined` while the new key is in flight, the list is momentarily empty, and an emptiness that
 * lasted one frame took a batch that had taken twelve clicks to assemble. Even the eight rows the
 * new filter still drew came back untinted, and unticking brought nothing back.
 *
 * So pruning is a *transition* rather than a membership test: an id is dropped only when it was in
 * the list a moment ago and is not in it now, under a list that is still the same list. That is
 * exactly the case the rule exists for — a selected row completed, dropped or deleted must never
 * stay in `targets` — and it is the only case it can see honestly.
 *
 * `viewChanged` is the caller saying "these ids answer a different question than the last ones".
 * Nothing is pruned on such a tick, and an id that is absent from the new view is *retained*,
 * because the task is still there and the user still means it. The cost is real and deliberate: a
 * task deleted elsewhere while a filter hides it stays selected until the filter widens. The bar
 * states the hidden count for exactly that reason — see `TaskCommandBar`.
 */
export function retainSelection(
  selected: ReadonlySet<string>,
  ids: readonly string[],
  previousIds: readonly string[],
  viewChanged: boolean,
): ReadonlySet<string> {
  if (selected.size === 0 || viewChanged) return selected;
  const live = new Set(ids);
  const was = new Set(previousIds);
  // Kept if the list still draws it, or if this list never drew it — the second clause is what
  // holds a row the filter is hiding.
  const next = new Set([...selected].filter((id) => live.has(id) || !was.has(id)));
  return next.size === selected.size ? selected : next;
}

/** The inclusive run of ids between two of them, in list order. Empty if either is absent. */
export function rangeBetween(
  ids: readonly string[],
  fromId: string | null,
  toId: string,
): string[] {
  const to = ids.indexOf(toId);
  if (to === -1) return [];
  const from = fromId === null ? 0 : Math.max(ids.indexOf(fromId), 0);
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  return ids.slice(lo, hi + 1);
}

/** Clamped, not wrapped: holding a key down should stop at the end, not cycle forever. */
export function shiftedCursor(
  current: string | null,
  ids: readonly string[],
  delta: number,
): string | null {
  if (ids.length === 0) return null;
  const at = current === null ? -1 : ids.indexOf(current);
  return ids[Math.min(Math.max(at + delta, 0), ids.length - 1)]!;
}

/**
 * The one task the command bar may name: the selection when it is exactly one row, else nobody.
 *
 * Both bars — the list's and the board's — used to pass the task under the *cursor*, which agrees
 * with the selection only until the cursor moves. Tick "Reply to the tiling quote", press `j j`,
 * and the bar read "Cancel the gym membership I never use · Start · To inbox": the cursor row's
 * title, and the verb set chosen by the cursor row's status, while every button acted on the
 * ticked row (`01a0336b-a098`). The bar renders only when something is selected, so the cursor is
 * never the right answer here; it is `targets` that the buttons act on, and with one row selected
 * that is this row.
 *
 * A selected row the filter has hidden is not in `tasks`, so this is null — and the bar is already
 * on its "1 selected · 1 hidden by the filter" sentence, which is the right one.
 */
export function soleSelectedTask<T extends { id: string }>(
  selectedIds: ReadonlySet<string>,
  tasks: readonly T[] | undefined,
): T | null {
  if (selectedIds.size !== 1) return null;
  const [id] = selectedIds;
  return tasks?.find((t) => t.id === id) ?? null;
}

export interface TaskSelection {
  /** The task under the cursor, or null when the list is empty. */
  cursorId: string | null;
  /** Every selected id. Empty means "just the cursor" — see `targets`. */
  selectedIds: ReadonlySet<string>;
  /**
   * What a command should act on: the explicit selection if there is one, otherwise the cursor
   * alone. Callers never have to special-case an empty selection.
   */
  targets: string[];
  move: (delta: number) => void;
  /**
   * Put the cursor on a row the user has just pointed at — clicked, or opened the detail of.
   *
   * An id this list does not draw parks the cursor instead of following it off-screen: see the
   * implementation, and `holdGap` for the same bargain reached from the other direction.
   */
  moveTo: (id: string) => void;
  toggle: (id: string) => void;
  /** Extends the selection from the cursor to `id`, for shift-click. */
  extendTo: (id: string) => void;
  /**
   * The same extension, a row at a time — `Shift-j` and `Shift-k`.
   *
   * The mouse had `Shift-click` and the keyboard had nothing, so the only way to build a batch
   * was to reach for the pointer: `Space` on every row is fifty keystrokes for the job six clicks
   * did. It grows the selection and never shrinks it, exactly as `extendTo` does — walking back
   * over a range you just took does not release it — because those two are the same gesture
   * reached by different devices and disagreeing about that would be worse than either rule.
   */
  extendBy: (delta: number) => void;
  clear: () => void;
  /** Every row this list is drawing. */
  selectAll: () => void;
  /**
   * Let the cursor's row vanish without another row inheriting the keystroke.
   *
   * `reconcileCursor` deliberately lands the cursor on whatever takes the vanished row's place,
   * which is right when the *list* changed underneath you and wrong when *you* removed the row: one
   * press of `x` completed the reviewer's probe task, the row went with no toast and no undo, the
   * cursor slid down, and a second press — the natural undo instinct — completed
   * `Spike: Keycloak vs Authentik`, which unblocked a third task. One keystroke moved three
   * counters and they restored it by hand.
   *
   * So an action that removes rows parks the cursor in the gap instead. Nothing is under it, so
   * the next `x` has nothing to act on; `j` or `k` resumes from where the row was.
   *
   * The device it was aimed with is not part of that rule, and used to be by accident: only the
   * keyboard path armed this, so clicking a row's checkbox completed it, the cursor slid onto
   * whatever moved up, and the next `x` acted on a task the user had never pointed at. Every path
   * that removes *the row you aimed at* arms it now — the checkbox, the row menu, the keys.
   *
   * `id` is for callers that arm in the same tick they move the cursor. A mouse press does both:
   * `moveTo` puts the cursor on the row and the control acts on it, and a `cursorId` read from
   * this callback's closure is still the previous row at that moment. Omit it for the keyboard,
   * where the cursor was already where it is.
   */
  holdGap: (id?: string) => void;
}

/**
 * Keep the keyboard cursor on whatever task the detail panel is showing.
 *
 * The app kept two notions of "the task I am working on" and they agreed only until you touched
 * the mouse: clicking a title opened its quick view and left `.is-cursor` where it was, so `x`,
 * `1`, `2` and `3` — every mutating key the `?` sheet documents — fired at a different row. Two
 * agents completed real tasks that way in one afternoon, one of them while reading a banner naming
 * a task it had never pointed at.
 *
 * Doing it here, from the id the panel is open on, rather than in each click handler, is what makes
 * the paths agree: the title button, the parent arrow, the row menu's *Open*, a subtask followed
 * from inside the panel and a task reached from global search all end at the same piece of state,
 * and there is exactly one rule reading it.
 *
 * It only ever moves the cursor. Opening and closing stay the panel's business, so the two cannot
 * drive each other in a loop — and closing the panel deliberately leaves the cursor alone, because
 * dismissing a view is not a statement about where you are in the list.
 */
export function useCursorFollowsOpenTask(
  selection: TaskSelection,
  ids: readonly string[],
  openTaskId: string | null | undefined,
): void {
  const { moveTo } = selection;
  /*
    The last id this rule aimed at, so it fires once per opened task and not once per render.

    `ids` has to be a dependency — on a remount the panel is already open while the query is still
    empty, and an effect keyed on `openTaskId` alone would have nothing to point at and never look
    again. But re-running it on every list change without this guard would drag the cursor back to
    the open task after every `j`, on a list that refetches every ten seconds.
  */
  const aimed = useRef<string | null>(null);
  useEffect(() => {
    if (openTaskId === undefined || openTaskId === null) {
      /*
        Nothing is open, so there is nothing to still be aimed at — and the memory has to go with
        the panel, not outlive it.

        It used to outlive it, and re-opening *the same* task then read as "already aimed": the
        line below returned before the parking branch ran, so the cursor stayed wherever the keys
        had left it. Follow a parent arrow, close the panel, press `j`, follow the same arrow
        again, and the panel named "Rewire the workshop" while `x` completed "Book the sparky" —
        the exact loss this rule was added to remove, on its own second run.

        Clearing here rather than dropping the ref: the ref is what stops a ten-second refetch
        dragging the cursor back onto the open task after every `j`, and that guard is only needed
        while a panel is open. Closed, there is nothing left to guard against.
      */
      aimed.current = null;
      return;
    }
    if (aimed.current === openTaskId) return;
    // Still loading: wait for the rows rather than parking against an empty list.
    if (ids.length === 0) return;
    aimed.current = openTaskId;
    moveTo(openTaskId);
  }, [openTaskId, ids, moveTo]);
}

/**
 * @param ids The rows the list is drawing, in the order it draws them.
 * @param listKey What question those rows answer — the filter, the sort, the search. Compared by
 *   identity, so pass a primitive: a serialised filter is what both task views use. When it
 *   changes, the ids arriving are a different view of the same tasks and the selection is carried
 *   across whole rather than pruned to what is now on screen. Omitting it makes every id list a
 *   continuation of the last one, which is right for a surface whose rows only come and go because
 *   tasks do.
 */
export function useTaskSelection(ids: readonly string[], listKey?: string): TaskSelection {
  const [cursorId, setCursorId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  // The previous id list, so a vanished cursor can be replaced by whatever took its place.
  const previous = useRef<readonly string[]>([]);
  /*
    Where the cursor was when it was parked, and whether it is parked at all — see `holdGap`.

    A ref rather than state: nothing renders differently for a parked cursor beyond there being no
    cursor row, and the reconciliation below has to read it in the same tick the ids change.
  */
  const gap = useRef<{ at: number } | null>(null);
  /*
    The id a keyboard action was aimed at, until the next id list arrives.

    Parking has to be conditional on the row actually leaving. A status change that keeps the row
    in view — `3` on an unfiltered list moves it to in_progress and it stays — must not take the
    cursor off it, or the next keystroke does nothing and the list feels broken. So `holdGap` only
    *arms*, and the reconciliation below parks if the row has gone and stands down if it has not.
  */
  const pending = useRef<string | null>(null);
  /*
    Where the cursor is, readable from a callback that must not be rebuilt when it moves.

    `moveTo` needs the current position to park against, and taking it as a dependency would give
    the callback a new identity on every keystroke — which `useCursorFollowsOpenTask` depends on
    and would then re-run on every one of them.
  */
  const placed = useRef<string | null>(null);
  useEffect(() => {
    placed.current = cursorId;
  }, [cursorId]);

  /* The `listKey` these ids came with, so the effect can tell a narrowed view from a lost row. */
  const view = useRef<string | undefined>(listKey);

  useEffect(() => {
    const live = new Set(ids);
    const viewChanged = view.current !== listKey;
    view.current = listKey;
    /*
      Read out of the ref *now*, not inside the updaters below.

      A `setState(fn)` updater runs when React computes the next state, which is after this effect
      body has already moved `previous.current` on to `ids` — so an updater reading the ref sees the
      new list as the old one, and every rule here that compares the two collapses. It cost an hour:
      pruning silently became a no-op, and `x` on a two-row selection left the bar claiming both
      completed tasks as "hidden by the filter". React's eager-state optimisation evaluates the
      updater inline often enough to hide it in a hand test.
    */
    const prior = previous.current;
    // Drop selected ids that have left the list, so a command can never act on a deleted task —
    // and only those. See `retainSelection` for why that is a transition and not a membership test.
    const keep = (current: ReadonlySet<string>) =>
      retainSelection(current, ids, prior, viewChanged);

    const armed = pending.current;
    if (armed !== null) {
      pending.current = null;
      if (!live.has(armed)) {
        // The row went, and this keystroke is what removed it: park rather than inherit.
        const at = prior.indexOf(armed);
        if (at !== -1) gap.current = { at };
        setCursorId(null);
        previous.current = ids;
        setSelectedIds(keep);
        return;
      }
    }

    setCursorId((current) => {
      if (gap.current !== null) {
        // Already parked: stay in the gap rather than inheriting whatever moved up into it.
        return null;
      }
      /*
        The cursor's answer to the same question is already decided and is deliberately not this
        one: it lands on whatever occupies the vanished row's position, because a cursor is a place
        in the list and a place always exists. A selection is a set of tasks, and a task the filter
        is hiding has not stopped being one.
      */
      return reconcileCursor(current, ids, prior);
    });

    setSelectedIds(keep);

    previous.current = ids;
  }, [ids, listKey]);

  const move = useCallback(
    (delta: number) => {
      if (ids.length === 0) return;
      /*
        The park is read and cleared **here**, not inside the updater below.

        It was inside, and that made the updater impure — it answered differently the second time it
        was called, because the first call had already set `gap.current` to null. React is explicit
        that an updater may be invoked more than once, and `react-dom@19.2.8`'s
        `updateReducerImpl` does exactly that in development:

            shouldDoubleInvokeUserFnsInHooksDEV && reducer(pendingQueue, updateLane);
            pendingQueue = update.hasEagerState ? update.eagerState : reducer(pendingQueue, ...);

        — one extra call whose result is *discarded*, then the real one. Which call's answer
        survives decides the behaviour, and it depends on `hasEagerState`: when the fiber has no
        pending `setCursorId` update, React computes the state eagerly at dispatch, so the first
        (correct) call wins and the discarded second one is harmless. When something else is
        already pending on that fiber — a refetch settling, the ten-second poll, the
        `invalidateQueries()` storm every status write kicks off — there is no eager state, so the
        *second* call is the one kept: it finds `gap.current` already null and falls through to
        `shiftedCursor(null, ids, delta)`, which is row one.

        Both halves were measured in this app rather than argued. A `console.warn` in the old
        updater, running `taskSelection.e2e.spec.ts` on an idle box, printed the two calls every
        time:

            PROBE updater call gap={"at":1} current=null     → 'Third', and clears the gap
            PROBE updater call gap=null     current=null     → shiftedCursor(null, …) = 'First'

        and the test passed, because the eager path kept the first. Giving the fiber a pending
        update in the `j` handler (`setFilter((f) => ({ ...f }))`, one temporary line) turned the
        eager path off and the same two calls then failed it with `Received string: "First"` —
        byte-identical to pipeline 685 / job 3010, where `taskSelection.e2e.spec.ts:186` expected
        "Third", got "First" on both failed attempts, 24 polls of a stable wrong value, no
        timeout, on a shard sharing twelve cores with `unit`, `integration` and the other shard. A
        busy shard is a busy fiber. It passed on attempt 3 and `retries: 2` called the shard green.

        `StrictMode` is what makes the extra call happen at all, so a production build is nearly
        immune; nearly, because React may also replay an interrupted render's queue, and that
        replay is the same second call with the gap already gone.

        Reading a ref inside an updater is fine — it answers the same way every time. Writing one
        is not. `extendBy` below has always done it this way, which is why `Shift-j` never flaked.
      */
      const parked = gap.current;
      gap.current = null;
      if (parked !== null) {
        /* Resuming from the gap: the row that took the vanished one's place, so `j` after an `x`
           goes where the eye already is rather than back to the top of the list. */
        setCursorId(ids[Math.min(Math.max(parked.at, 0), ids.length - 1)] ?? null);
        return;
      }
      setCursorId((current) => shiftedCursor(current, ids, delta));
    },
    [ids],
  );

  const holdGap = useCallback(
    (id?: string) => {
      // Armed only — see `pending`. Whether the cursor parks depends on the row actually leaving.
      pending.current = id ?? cursorId;
    },
    [cursorId],
  );

  const moveTo = useCallback(
    (id: string) => {
      // Pointing at a row is the user saying where they are, so the gap goes.
      if (ids.includes(id)) {
        gap.current = null;
        setCursorId(id);
        return;
      }
      /*
        Pointed at something this list does not draw — a subtask opened from the quick view, a
        parent the filter hides, a task reached from global search.

        Leaving the cursor where it was is the defect this whole change exists to remove: the user
        is looking at one task while `x`, `1`, `2` and `3` are aimed at another. There is no row to
        move to, so park instead. Nothing is under the cursor, so a mutating key has no target at
        all, and `j`/`k` resume from where the eye last was — the same bargain `holdGap` strikes.

        Parking has to go through `gap`, not a bare `setCursorId(null)`: the reconciliation above
        reads a null cursor as "not placed yet" and hands it `ids[0]`, so an unparked null would
        quietly become row one on the next refetch — which is precisely the wrong-row hazard again,
        ten seconds later and with nothing on screen to explain it.
      */
      // Already parked, or never placed: leave the gap holding whatever position it holds.
      const at = placed.current === null ? -1 : ids.indexOf(placed.current);
      if (at !== -1) gap.current = { at };
      setCursorId(null);
    },
    [ids],
  );

  const toggle = useCallback((id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    /*
      And the row you just clicked becomes the anchor.

      `extendTo` extends from `cursorId`, and this never set it — so shift-click extended from
      wherever the keyboard cursor last landed, which on a freshly loaded list is row one.
      Ctrl-clicking row 21 and shift-clicking row 26 selected twenty-six rows, and the bar said so
      while the user believed they had six. Pressing `j` first made it behave, which is what
      pointed at the cause.

      Same reasoning as `moveTo` directly above: pointing at a row is the user saying where they
      are. Set on deselect too — you are still saying where you are — and the gap is cleared for
      the same reason it is cleared there.
    */
    gap.current = null;
    setCursorId(id);
  }, []);

  const extendTo = useCallback(
    (id: string) => {
      setSelectedIds((current) => new Set([...current, ...rangeBetween(ids, cursorId, id)]));
      setCursorId(id);
    },
    [cursorId, ids],
  );

  const extendBy = useCallback(
    (delta: number) => {
      if (ids.length === 0) return;
      /*
        Resuming from a parked cursor goes where `move` goes — the row that took the vanished
        one's place — and takes that row alone. There is no anchor to extend from when nothing was
        under the cursor, and reading `cursorId` (null while parked) would hand `rangeBetween` a
        null `from`, which means "start at the top": one `Shift-j` after an `x` would have selected
        every row above the gap.
      */
      const parked = gap.current;
      const to =
        parked !== null
          ? (ids[Math.max(Math.min(parked.at, ids.length - 1), 0)] ?? null)
          : shiftedCursor(cursorId, ids, delta);
      if (to === null) return;
      gap.current = null;
      const from = parked !== null ? to : cursorId;
      setSelectedIds((current) => new Set([...current, ...rangeBetween(ids, from, to)]));
      setCursorId(to);
    },
    [cursorId, ids],
  );

  const clear = useCallback(() => setSelectedIds(new Set()), []);
  const selectAll = useCallback(() => setSelectedIds(new Set(ids)), [ids]);

  const targets = selectedIds.size > 0 ? [...selectedIds] : cursorId === null ? [] : [cursorId];

  return {
    cursorId,
    selectedIds,
    targets,
    move,
    moveTo,
    toggle,
    extendTo,
    extendBy,
    clear,
    selectAll,
    holdGap,
  };
}
